import { useEffect, useRef, useCallback } from "react";
import mqtt, { IClientOptions, MqttClient } from "mqtt";
import { toastHandler } from "@/lib/ToastHandler";

interface DriverUpdateMessage {
  type: "DRIVER_UPDATE";
  driverId: string;
  updatedBy: string;
  timestamp: number;
  user: string;
}

interface UseDriverSyncProps {
  driverId: string;
  userNickname: string;
  onDriverUpdate?: (message: DriverUpdateMessage) => void;
}

// Generate a unique session ID for this browser tab
function getSessionId() {
  let sessionId = sessionStorage.getItem("driver_sync_session_id");
  if (!sessionId) {
    sessionId = Math.random().toString(36).substr(2, 9) + Date.now();
    sessionStorage.setItem("driver_sync_session_id", sessionId);
  }
  return sessionId;
}

export function useDriverSync({
  driverId,
  userNickname,
  onDriverUpdate,
}: UseDriverSyncProps) {
  const clientRef = useRef<MqttClient | null>(null);
  // Store a unique session ID for this tab/browser
  const sessionIdRef = useRef<string>(getSessionId());

  // Flag to ensure only one info toast per message
  const shownMessageIdsRef = useRef<Set<string>>(new Set());

  // MQTT connection options (same as DriverDetail.tsx)
  const getMqttOptions = useCallback(
    (): IClientOptions => ({
      clientId: `web_driver_updates_${driverId}_${userNickname}_${Math.random().toString(16).slice(3)}`,
      clean: true,
      protocol: "wss",
      path: "/mqtt",
      rejectUnauthorized: true,
      keepalive: 60,
      reconnectPeriod: 5000,
      connectTimeout: 30 * 1000,
      username: import.meta.env.VITE_MQTT_USERNAME,
      password: import.meta.env.VITE_MQTT_PASSWORD,
    }),
    [driverId, userNickname],
  );

  // Handle incoming driver update messages
  const handleMessage = useCallback(
    (_: unknown, message: Buffer) => {
      try {
        const data: DriverUpdateMessage & { sessionId?: string } = JSON.parse(
          message.toString(),
        );
        // Generate a unique message ID (by timestamp + user + driverId)
        const messageId = `${data.driverId}_${data.timestamp}_${data.user}`;

        // Only process driver update messages for this driver
        if (data.type === "DRIVER_UPDATE" && data.driverId === driverId) {
          // Only notify if it comes from a DIFFERENT user AND DIFFERENT session
          // Don't notify if same user from same session (self-update)
          const isSameUser = data.user === userNickname;
          const isSameSession = data.sessionId === sessionIdRef.current;
          const shouldNotify = !(isSameUser && isSameSession);

          // Only show info toast once per message
          if (shouldNotify && !shownMessageIdsRef.current.has(messageId)) {
            toastHandler({
              title: "La información del conductor ha sido actualizada",
              description: `Refresca la página para ver los cambios.`,
              variant: "info",
            });

            shownMessageIdsRef.current.add(messageId);

            // Call custom callback if provided
            onDriverUpdate?.(data);
          } else if (!shouldNotify) {
            // Ignoring self-update from same session
          } else {
            // Ignoring duplicate notification for this message
          }
        }
      } catch (error) {
        console.error("❌ Error parsing MQTT message:", error);
      }
    },
    [driverId, userNickname, onDriverUpdate], // Removed toast from dependencies
  );

  // Connect to MQTT broker
  const connect = useCallback(() => {
    if (clientRef.current?.connected) {
      return;
    }

    try {
      const options = getMqttOptions();
      const client: MqttClient = mqtt.connect(
        import.meta.env.VITE_MQTT_BROKER_URL,
        options,
      );

      clientRef.current = client;

      // Topic for driver update notifications
      const updateTopic = `drivers/${driverId}/updates`;

      client.on("connect", () => {
        // MQTT connected for driver updates

        // Subscribe to driver update notifications
        client.subscribe(updateTopic, { qos: 1 }, (err) => {
          if (err) {
            console.error("❌ Error subscribing to MQTT topic:", err);
          }
        });
      });

      client.on("message", handleMessage);

      client.on("error", (err: Error) => {
        console.error("❌ MQTT connection error:", err);
      });

      client.on("reconnect", () => {
        // MQTT reconnecting
      });

      client.on("offline", () => {
        // MQTT offline
      });

      client.on("close", () => {
        // MQTT connection closed
      });
    } catch (error) {
      console.error("💥 Failed to create MQTT connection:", error);
    }
  }, [driverId, getMqttOptions, handleMessage]);

  // Disconnect from MQTT
  const disconnect = useCallback(() => {
    if (clientRef.current) {
      if (clientRef.current.connected) {
        clientRef.current.end();
      }
      clientRef.current = null;
    }
  }, []);

  // Debounce for sending notifications to prevent spam
  const lastSendRef = useRef<number>(0);
  const SEND_DEBOUNCE_MS = 1000; // 1 second

  // Notify other users about driver update
  const notifyDriverUpdate = useCallback(
    (updatedBy: string) => {
      if (!driverId || !userNickname) {
        console.warn("⚠️ Cannot send update: missing driverId or userNickname");
        return;
      }

      if (!clientRef.current?.connected) {
        console.warn("⚠️ Cannot send update: MQTT not connected");
        return;
      }

      // Debounce sending to prevent multiple rapid notifications
      const now = Date.now();
      const timeSinceLastSend = now - lastSendRef.current;

      if (timeSinceLastSend < SEND_DEBOUNCE_MS) {
        // Ignoring duplicate send (debounced)
        return;
      }

      const updateTopic = `drivers/${driverId}/updates`;
      const updateMessage: DriverUpdateMessage & { sessionId: string } = {
        type: "DRIVER_UPDATE",
        driverId,
        updatedBy,
        timestamp: now,
        user: userNickname,
        sessionId: sessionIdRef.current,
      };

      try {
        clientRef.current.publish(
          updateTopic,
          JSON.stringify(updateMessage),
          { qos: 1 },
          (error) => {
            if (error) {
              console.error("❌ Error publishing MQTT message:", error);
            } else {
              // Published to MQTT topic
              lastSendRef.current = now;
            }
          },
        );
      } catch (error) {
        console.error("💥 Failed to publish MQTT message:", error);
      }
    },
    [driverId, userNickname],
  );

  // Connect on mount, disconnect on unmount
  useEffect(() => {
    if (driverId && userNickname) {
      connect();
    }

    return () => {
      disconnect();
    };
  }, [driverId, userNickname, connect, disconnect]);

  return {
    notifyDriverUpdate,
    isConnected: clientRef.current?.connected || false,
    connect,
    disconnect,
  };
}
